import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, User } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, isArabic } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('home') },
    { to: '/properties', label: t('properties') },
    { to: '/architecture', label: t('architecture') },
    { to: '/services', label: t('services') },
    { to: '/blog', label: isArabic ? 'المدونة' : 'Blog' },
    { to: '/about', label: t('about') },
    { to: '/contact', label: t('contact') }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
      style={{ zIndex: 9999 }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className={`flex items-center ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <img 
              src="https://customer-assets.emergentagent.com/job_harry-and-jacobs/artifacts/7nr8z83x_Insta%20Profile%20Pic.jpg" 
              alt="Harry & Jacobs Logo" 
              className="h-14 w-14 rounded-full"
            />
            <div className="hidden sm:block">
              <div className="font-bold text-[#404040] text-lg">Harry & Jacobs</div>
              <div className="text-xs text-gray-600">{isArabic ? 'وكلاء العقارات' : 'Estate Agents'}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center ${isArabic ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link font-medium ${
                  isActive(link.to) ? 'text-[#FF8C42] active' : 'text-[#404040] hover:text-[#FF8C42]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Contact Info & Language Switcher & CTA */}
          <div className={`hidden lg:flex items-center ${isArabic ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            <LanguageSwitcher />
            <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-2' : 'space-x-2'} text-[#404040]`}>
              <Phone className="w-4 h-4 text-[#FF8C42]" />
              <span className="text-sm font-medium">020 8189 8889</span>
            </div>
            <Link to="/signin">
              <Button variant="outline" className="border-[#404040] text-[#404040] hover:bg-[#404040] hover:text-white">
                <User className="w-4 h-4 mr-2" />
                {isArabic ? 'تسجيل الدخول' : 'Sign In'}
              </Button>
            </Link>
            <Link to="/valuation">
              <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white btn-hover-lift">
                {t('getFreeValuation')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#404040] hover:text-[#FF8C42]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4 fade-in">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-medium py-2 ${
                    isActive(link.to) ? 'text-[#FF8C42]' : 'text-[#404040] hover:text-[#FF8C42]'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-gray-200 pt-3 mt-2">
                <Link
                  to="/signin"
                  className="flex items-center gap-2 font-medium py-2 text-[#404040] hover:text-[#FF8C42]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4" />
                  {isArabic ? 'تسجيل الدخول' : 'Sign In / Portals'}
                </Link>
              </div>
              <div className="pt-2">
                <LanguageSwitcher />
              </div>
              <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-2' : 'space-x-2'} text-[#404040] py-2`}>
                <Phone className="w-4 h-4 text-[#FF8C42]" />
                <span className="text-sm">020 8189 8889</span>
              </div>
              <Link to="/valuation" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                  {t('getFreeValuation')}
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
